/**
 * Discovery offer detail BottomSheet — OverlayBackDismiss while open
 * (DirectoryFilterSheet peer chrome).
 */

import { useEffect, useId, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOptionalOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';
import { BottomSheet } from '../../shared/ui/BottomSheet.js';
import { Button } from '../../shared/ui/Button.js';
import { AdaptiveAddToCartLabel } from '../../shared/ui/AdaptiveAddToCartLabel.js';
import type { FormatDistanceMetersLabels } from '../../shared/ui/directoryDistance.js';
import type { PublicDiscoveryOfferItemDTO } from '../discovery/publicDiscoveryTypes.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { formatOfferDistance } from './browseOfferPresentation.js';
import { DiscoveryOfferMedia } from './DiscoveryOfferMedia.js';
import { OfferBagInButton } from './OfferBagInButton.js';

export type DiscoveryOfferDetailContext = 'flat' | 'grouped';

/** Browse discovery vs in-store shop catalog (same sheet chrome). */
export type DiscoveryOfferDetailSurface = 'browse' | 'shop';

export interface DiscoveryOfferDetailSheetProps {
  readonly open: boolean;
  readonly offer: PublicDiscoveryOfferItemDTO | null;
  readonly detailContext?: DiscoveryOfferDetailContext;
  /**
   * `shop` — in-store catalog detail: hide go-to-shop / distance / org meta;
   * single Add CTA (shop AdaptiveAddToCartLabel defaults).
   */
  readonly surface?: DiscoveryOfferDetailSurface;
  /** BottomSheet root test id (shop may pass `customer-shop-product-detail`). */
  readonly testId?: string;
  readonly onClose: () => void;
  readonly formatPrice: (amountMajor: number) => string;
  readonly distanceFormatter: FormatDistanceMetersLabels;
  readonly onAdd: (offer: PublicDiscoveryOfferItemDTO) => void;
  readonly onGoToShop: (offer: PublicDiscoveryOfferItemDTO) => void;
  /** Optional footer slot below primary CTA (e.g. P6 restock alert on OOS). */
  readonly belowCta?: ReactNode;
}

const DETAIL_CLOSE_BUTTON_CLASS = [
  'inline-flex h-12 min-h-12 w-12 min-w-12 shrink-0 items-center justify-center',
  'rounded-full border border-[var(--color-border)]',
  'bg-[var(--color-surface-elevated)]/90 text-[var(--color-on-surface-muted)]',
  'backdrop-blur-sm',
  'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--color-focus-ring)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

function resolveDiscoveryOfferShopHref(
  offer: PublicDiscoveryOfferItemDTO,
): string | undefined {
  const explicitUrl =
    typeof offer.salesPointContext.customerShopUrl === 'string'
      ? offer.salesPointContext.customerShopUrl.trim()
      : '';
  const fallbackUrl =
    explicitUrl.length > 0
      ? explicitUrl
      : `/${encodeURIComponent(offer.tenantContext.tenantCode)}/${encodeURIComponent(offer.salesPointContext.code)}`;
  return resolveDirectoryShopOpenAction({
    customerShopUrl: fallbackUrl,
    targetTenantCode: offer.tenantContext.tenantCode,
  })?.url;
}

function DetailMetaRow(props: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}): JSX.Element {
  const { label, value, testId } = props;
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
      <span className="shrink-0 text-xs font-medium text-[var(--color-on-surface-muted)]">
        {label}
      </span>
      <span
        className="min-w-0 text-end text-sm leading-snug text-[var(--color-on-surface)]"
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

function DetailSectionHeading(props: { readonly children: string }): JSX.Element {
  return (
    <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]">
      {props.children}
    </h3>
  );
}

export function DiscoveryOfferDetailSheet(
  props: DiscoveryOfferDetailSheetProps,
): JSX.Element {
  const {
    open,
    offer,
    detailContext = 'flat',
    surface = 'browse',
    testId = 'customer-browse-offer-detail',
    onClose,
    formatPrice,
    distanceFormatter,
    onAdd,
    onGoToShop,
    belowCta,
  } = props;
  const { t } = useTranslation('customer');
  const titleId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;
  const isShopSurface = surface === 'shop';
  const testIdPrefix = isShopSurface
    ? 'customer-shop-product-detail'
    : 'customer-browse-offer-detail';

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  const title =
    offer !== null && offer.title.trim().length > 0
      ? offer.title
      : t('browse.discovery.detailSheetTitle');

  const distanceLabel =
    offer !== null && !isShopSurface && detailContext === 'flat'
      ? formatOfferDistance(offer, distanceFormatter)
      : null;
  const addDisabled = offer === null || offer.price === null;
  const description =
    offer !== null && typeof offer.description === 'string'
      ? offer.description.trim()
      : '';
  const showGroupedCategoryOnly =
    !isShopSurface &&
    detailContext === 'grouped' &&
    offer?.platformCategory !== null;
  const showFullMeta = !isShopSurface && detailContext === 'flat';
  const goToShopHref = useMemo(
    () =>
      !isShopSurface && offer !== null
        ? resolveDiscoveryOfferShopHref(offer)
        : undefined,
    [isShopSurface, offer],
  );

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      titleId={titleId}
      hideHeader
      contentPadding="flush"
      panelLayout="flex"
      closeLabel={t('browse.discovery.detailCloseAria')}
      testId={testId}
      className="max-h-[90vh] bg-[var(--color-surface-elevated)] lg:max-w-lg"
      containerClassName="lg:items-center"
    >
      {offer !== null ? (
        <div
          className="flex min-h-0 flex-1 flex-col"
          data-testid={`${testIdPrefix}-body`}
        >
          <div className="relative shrink-0">
            <DiscoveryOfferMedia
              offer={offer}
              testId={`${testIdPrefix}-media`}
              aspect="4/3"
              imageAlt={offer.title}
            />
            <OfferBagInButton
              offer={offer}
              testId={`${testIdPrefix}-bag-in`}
              className={[
                'absolute start-3 top-3 z-[1]',
                'border border-[var(--color-border)]',
                'bg-[var(--color-surface-elevated)]/90 backdrop-blur-sm',
                'hover:bg-[var(--color-surface-hover)]',
              ].join(' ')}
            />
            <button
              type="button"
              aria-label={t('browse.discovery.detailCloseAria')}
              onClick={onClose}
              data-testid={`${testIdPrefix}-close`}
              className={`absolute end-3 top-3 z-[1] ${DETAIL_CLOSE_BUTTON_CLASS}`}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-4 px-5 py-4">
              <h2
                className="m-0 text-lg font-semibold leading-tight text-[var(--color-on-surface)]"
                data-testid={`${testIdPrefix}-title`}
              >
                {offer.title}
              </h2>

              <section className="flex min-w-0 flex-col gap-1.5">
                <DetailSectionHeading>
                  {t('browse.discovery.detailPriceLabel')}
                </DetailSectionHeading>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  {offer.price !== null ? (
                    <span
                      className="text-2xl font-semibold tabular-nums tracking-tight text-[var(--color-on-surface)]"
                      data-testid={`${testIdPrefix}-price`}
                    >
                      {formatPrice(offer.price)}
                    </span>
                  ) : (
                    <span
                      className="text-sm text-[var(--color-on-surface-muted)]"
                      data-testid={`${testIdPrefix}-price`}
                    >
                      {t('browse.discovery.priceUnavailable')}
                    </span>
                  )}
                  {distanceLabel !== null ? (
                    <span
                      className="text-sm tabular-nums text-[var(--color-on-surface-muted)]"
                      data-testid="customer-browse-offer-detail-distance"
                    >
                      {distanceLabel}
                    </span>
                  ) : null}
                </div>
              </section>

              {description.length > 0 ? (
                <section className="flex min-w-0 flex-col gap-1.5">
                  <DetailSectionHeading>
                    {t('browse.discovery.detailDescriptionLabel')}
                  </DetailSectionHeading>
                  <p
                    className="m-0 whitespace-pre-wrap break-words text-sm leading-relaxed text-[var(--color-on-surface)]"
                    data-testid={`${testIdPrefix}-description`}
                  >
                    {description}
                  </p>
                </section>
              ) : null}

              {offer.gpsr != null ? (
                <section
                  className="flex min-w-0 flex-col gap-2"
                  data-testid={`${testIdPrefix}-gpsr`}
                >
                  <DetailSectionHeading>
                    {t('browse.discovery.detailGpsrLabel', {
                      defaultValue: 'Product safety (GPSR)',
                    })}
                  </DetailSectionHeading>
                  <div className="rounded-xl border border-[var(--color-outline-variant)] bg-[var(--color-surface)] px-3 py-1">
                    <DetailMetaRow
                      label={t('browse.discovery.detailGpsrManufacturer', {
                        defaultValue: 'Manufacturer',
                      })}
                      value={offer.gpsr.manufacturerName}
                      testId={`${testIdPrefix}-gpsr-manufacturer`}
                    />
                    <div
                      className="border-t border-[var(--color-outline-variant)]"
                      aria-hidden="true"
                    />
                    <DetailMetaRow
                      label={t('browse.discovery.detailGpsrPostal', {
                        defaultValue: 'Postal address',
                      })}
                      value={offer.gpsr.manufacturerPostalAddress}
                      testId={`${testIdPrefix}-gpsr-postal`}
                    />
                    <div
                      className="border-t border-[var(--color-outline-variant)]"
                      aria-hidden="true"
                    />
                    <DetailMetaRow
                      label={t('browse.discovery.detailGpsrContact', {
                        defaultValue: 'Electronic contact',
                      })}
                      value={offer.gpsr.manufacturerElectronicContact}
                      testId={`${testIdPrefix}-gpsr-contact`}
                    />
                    <div
                      className="border-t border-[var(--color-outline-variant)]"
                      aria-hidden="true"
                    />
                    <DetailMetaRow
                      label={t('browse.discovery.detailGpsrProductId', {
                        defaultValue: 'Product ID',
                      })}
                      value={offer.gpsr.productIdentifier}
                      testId={`${testIdPrefix}-gpsr-product-id`}
                    />
                    {offer.gpsr.euResponsiblePerson !== null ? (
                      <>
                        <div
                          className="border-t border-[var(--color-outline-variant)]"
                          aria-hidden="true"
                        />
                        <DetailMetaRow
                          label={t('browse.discovery.detailGpsrEuResponsible', {
                            defaultValue: 'EU responsible person',
                          })}
                          value={`${offer.gpsr.euResponsiblePerson.name} — ${offer.gpsr.euResponsiblePerson.postalAddress} — ${offer.gpsr.euResponsiblePerson.electronicContact}`}
                          testId={`${testIdPrefix}-gpsr-eu-responsible`}
                        />
                      </>
                    ) : null}
                    {offer.gpsr.safetyInfo !== null &&
                    offer.gpsr.safetyInfo.trim().length > 0 ? (
                      <>
                        <div
                          className="border-t border-[var(--color-outline-variant)]"
                          aria-hidden="true"
                        />
                        <DetailMetaRow
                          label={t('browse.discovery.detailGpsrSafety', {
                            defaultValue: 'Safety information',
                          })}
                          value={offer.gpsr.safetyInfo}
                          testId={`${testIdPrefix}-gpsr-safety`}
                        />
                      </>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {showFullMeta ? (
                <section className="flex min-w-0 flex-col gap-2">
                  <DetailSectionHeading>
                    {t('browse.discovery.detailMetaLabel')}
                  </DetailSectionHeading>
                  <div
                    className="rounded-xl border border-[var(--color-outline-variant)] bg-[var(--color-surface)] px-3 py-1"
                    data-testid="customer-browse-offer-detail-meta"
                  >
                    <DetailMetaRow
                      label={t('browse.discovery.detailOrgLabel')}
                      value={offer.tenantContext.tenantName}
                      testId="customer-browse-offer-detail-org"
                    />
                    <div
                      className="border-t border-[var(--color-outline-variant)]"
                      aria-hidden="true"
                    />
                    <DetailMetaRow
                      label={t('browse.discovery.detailPlaceLabel')}
                      value={offer.salesPointContext.name}
                      testId="customer-browse-offer-detail-place"
                    />
                    {offer.platformCategory !== null ? (
                      <>
                        <div
                          className="border-t border-[var(--color-outline-variant)]"
                          aria-hidden="true"
                        />
                        <DetailMetaRow
                          label={t('browse.discovery.detailCategoryLabel')}
                          value={offer.platformCategory.name}
                          testId="customer-browse-offer-detail-category"
                        />
                      </>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {showGroupedCategoryOnly && offer.platformCategory !== null ? (
                <section className="flex min-w-0 flex-col gap-2">
                  <DetailSectionHeading>
                    {t('browse.discovery.detailCategoryLabel')}
                  </DetailSectionHeading>
                  <p
                    className="m-0 text-sm text-[var(--color-on-surface)]"
                    data-testid="customer-browse-offer-detail-category"
                  >
                    {offer.platformCategory.name}
                  </p>
                </section>
              ) : null}

              {/*
                H09-UI Home N/A — never mount SellerLegalIdentityShell here (browse or shop
                adapter). H09-TECH / B01 lives on ShopScreenView + checkout only.
              */}
            </div>
          </div>

          <div
            className="shrink-0 border-t border-[var(--color-outline-variant)] bg-[var(--color-surface-elevated)] px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            data-testid={`${testIdPrefix}-footer`}
          >
            {isShopSurface ? (
              <Button
                intent="primary"
                size="md"
                block
                type="button"
                disabled={addDisabled}
                data-testid={`${testIdPrefix}-add`}
                aria-label={
                  offer !== null
                    ? t('shop.addToCartAria', { name: offer.title })
                    : undefined
                }
                onClick={() => {
                  if (offer !== null && offer.price !== null) {
                    onAdd(offer);
                  }
                }}
              >
                <AdaptiveAddToCartLabel alwaysLong />
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                {goToShopHref !== undefined ? (
                  <Link
                    to={goToShopHref}
                    className="block w-full max-w-sm mx-auto no-underline"
                    data-testid="customer-browse-offer-detail-go-shop"
                    aria-label={
                      offer !== null
                        ? t('browse.discovery.goToShopAria', {
                            shop: offer.salesPointContext.name,
                          })
                        : undefined
                    }
                    onClick={() => {
                      if (offer !== null) {
                        onGoToShop(offer);
                      }
                    }}
                  >
                    <Button
                      intent="secondary"
                      size="md"
                      block
                      type="button"
                      tabIndex={-1}
                      className="pointer-events-none w-full"
                    >
                      {t('browse.discovery.goToShop')}
                    </Button>
                  </Link>
                ) : (
                  <Button
                    intent="secondary"
                    size="md"
                    block
                    type="button"
                    disabled
                    data-testid="customer-browse-offer-detail-go-shop"
                  >
                    {t('browse.discovery.goToShop')}
                  </Button>
                )}
                <Button
                  intent="primary"
                  size="md"
                  block
                  type="button"
                  disabled={addDisabled}
                  data-testid="customer-browse-offer-detail-add"
                  aria-label={
                    offer !== null
                      ? t('browse.discovery.addAria', { name: offer.title })
                      : undefined
                  }
                  onClick={() => {
                    if (offer !== null && offer.price !== null) {
                      onAdd(offer);
                    }
                  }}
                >
                  <AdaptiveAddToCartLabel
                    shortKey="browse.discovery.add"
                    longKey="browse.discovery.addToCart"
                    alwaysLong
                  />
                </Button>
              </div>
            )}
            {belowCta !== undefined && belowCta !== null ? (
              <div className="mt-3" data-testid={`${testIdPrefix}-below-cta`}>
                {belowCta}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </BottomSheet>
  );
}
